import React, { useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import styles from './ModuleRail.module.scss';
import type { ModuleId } from '../tokens/types';
import type { ModuleRailItem } from '../useAicaReportV2Logic';

const SearchIcon: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.4" />
        <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
);

const CheckTick: React.FC = () => (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M2.5 6.2L4.8 8.5L9.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const DragHandleIcon: React.FC = () => (
    <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        {[2, 8, 14].flatMap((y) => [
            <circle key={`l-${y}`} cx="2" cy={y} r="1.1" fill="currentColor" />,
            <circle key={`r-${y}`} cx="8" cy={y} r="1.1" fill="currentColor" />,
        ])}
    </svg>
);

const LockIcon: React.FC = () => (
    <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="2" y="7" width="10" height="7.5" rx="1" stroke="currentColor" strokeWidth="1.3" />
        <path d="M4.5 7V4.8C4.5 3.3 5.6 2 7 2C8.4 2 9.5 3.3 9.5 4.8V7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
);

export type ModuleRailProps = {
    items: ModuleRailItem[];
    searchTerm: string;
    setSearchTerm: (s: string) => void;
    toggleModule: (id: ModuleId) => void;
    selectAll: () => void;
    deselectAll: () => void;
    allEnabled: boolean;
    reorder: (id: ModuleId, targetId: ModuleId, position?: 'before' | 'after') => void;
    loading?: boolean;
};

const SkeletonCards: React.FC<{ count?: number }> = ({ count = 8 }) => (
    <>
        {Array.from({ length: count }).map((_, i) => (
            <div
                key={i}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: 12,
                    border: '1px solid #EDECE6',
                    borderRadius: 8,
                    background: '#FFFFFF',
                    marginBottom: 8,
                    opacity: 1 - i * 0.06,
                }}
            >
                <div
                    style={{
                        width: 10,
                        height: 16,
                        background: '#F0EFE9',
                        borderRadius: 2,
                        flexShrink: 0,
                    }}
                />
                <div style={{ flex: 1 }}>
                    <div
                        style={{
                            height: 8,
                            width: '40%',
                            background: '#F5F4EE',
                            borderRadius: 4,
                            marginBottom: 6,
                            animation: 'aicaShimmer 1.4s ease-in-out infinite',
                        }}
                    />
                    <div
                        style={{
                            height: 10,
                            width: '70%',
                            background: '#EDECE6',
                            borderRadius: 4,
                            animation: 'aicaShimmer 1.4s ease-in-out infinite',
                            animationDelay: '0.2s',
                        }}
                    />
                </div>
                <div
                    style={{
                        width: 32,
                        height: 18,
                        background: '#F0EFE9',
                        borderRadius: 9,
                        flexShrink: 0,
                    }}
                />
            </div>
        ))}
        <style>{`
            @keyframes aicaShimmer {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.55; }
            }
        `}</style>
    </>
);

type DropMark = { id: ModuleId; position: 'before' | 'after' } | null;

export const ModuleRail: React.FC<ModuleRailProps> = ({
    items,
    searchTerm,
    setSearchTerm,
    toggleModule,
    selectAll,
    deselectAll,
    allEnabled,
    reorder,
    loading = false,
}) => {
    const [dragId, setDragId] = useState<ModuleId | null>(null);
    const [dropMark, setDropMark] = useState<DropMark>(null);
    const lastMarkRef = useRef<string>('');
    const [tooltipId, setTooltipId] = useState<ModuleId | null>(null);
    const transparentGifRef = useRef<HTMLImageElement | null>(null);

    // FLIP animation refs — animate toggle-driven reorder. Drop-driven reorder skips animation for instant placement.
    const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const prevRectsRef = useRef<Map<string, DOMRect>>(new Map());
    const skipFlipRef = useRef(false);

    useLayoutEffect(() => {
        const prev = prevRectsRef.current;
        const next = new Map<string, DOMRect>();
        cardRefs.current.forEach((el, id) => {
            if (el && document.body.contains(el)) next.set(id, el.getBoundingClientRect());
        });

        if (skipFlipRef.current) {
            skipFlipRef.current = false;
            prevRectsRef.current = next;
            return;
        }

        next.forEach((newRect, id) => {
            const oldRect = prev.get(id);
            const el = cardRefs.current.get(id);
            if (!oldRect || !el) return;
            const dy = oldRect.top - newRect.top;
            if (Math.abs(dy) < 2) return;
            el.style.transition = 'none';
            el.style.transform = `translateY(${dy}px)`;
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            el.offsetHeight;
            requestAnimationFrame(() => {
                el.style.transition = 'transform 360ms cubic-bezier(0.2, 0.8, 0.2, 1)';
                el.style.transform = '';
            });
        });
        prevRectsRef.current = next;
    }, [items]);

    // Tiny transparent PNG used to suppress the browser's ghost image (cleaner FLIP-style feel).
    const getTransparentImage = (): HTMLImageElement => {
        if (transparentGifRef.current) return transparentGifRef.current;
        const img = new Image();
        img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        transparentGifRef.current = img;
        return img;
    };

    const onDragStart = (id: ModuleId, locked: boolean) => (e: React.DragEvent) => {
        if (locked) {
            e.preventDefault();
            return;
        }
        setDragId(id);
        try {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', id);
            e.dataTransfer.setDragImage(getTransparentImage(), 0, 0);
        } catch {
            /* noop */
        }
    };

    const dragOverRafRef = useRef<number | null>(null);
    const pendingMarkRef = useRef<{ id: ModuleId; clientY: number; rect: DOMRect } | null>(null);

    const onDragOver = (id: ModuleId) => (e: React.DragEvent<HTMLDivElement>) => {
        if (!dragId || dragId === id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        // Read layout synchronously (cheap because the row is already laid out) but defer the
        // setState to the next animation frame so rapid dragover events coalesce into one render.
        pendingMarkRef.current = {
            id,
            clientY: e.clientY,
            rect: (e.currentTarget as HTMLDivElement).getBoundingClientRect(),
        };
        if (dragOverRafRef.current != null) return;
        dragOverRafRef.current = requestAnimationFrame(() => {
            dragOverRafRef.current = null;
            const pending = pendingMarkRef.current;
            pendingMarkRef.current = null;
            if (!pending) return;
            const midY = pending.rect.top + pending.rect.height / 2;
            const position: 'before' | 'after' = pending.clientY < midY ? 'before' : 'after';
            const key = `${pending.id}:${position}`;
            if (lastMarkRef.current === key) return;
            lastMarkRef.current = key;
            setDropMark({ id: pending.id, position });
        });
    };

    const flushDragOverRaf = () => {
        if (dragOverRafRef.current != null) {
            cancelAnimationFrame(dragOverRafRef.current);
            dragOverRafRef.current = null;
        }
        pendingMarkRef.current = null;
    };

    const onDrop = (id: ModuleId) => (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        flushDragOverRaf();
        if (dragId && dropMark && dragId !== id) {
            skipFlipRef.current = true;
            reorder(dragId, dropMark.id, dropMark.position);
        }
        setDragId(null);
        setDropMark(null);
        lastMarkRef.current = '';
    };

    const onDragEnd = () => {
        flushDragOverRaf();
        setDragId(null);
        setDropMark(null);
        lastMarkRef.current = '';
    };

    const onListDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        // Only clear when leaving the whole list container, not just moving across children.
        if (!dragId) return;
        const rt = e.relatedTarget as Node | null;
        if (rt && (e.currentTarget as Node).contains(rt)) return;
        setDropMark(null);
        lastMarkRef.current = '';
    };

    return (
        <aside className={styles.Rail}>
            <div className={styles.SearchWrap}>
                <input
                    type="text"
                    value={searchTerm}
                    placeholder="Search Module"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    aria-label="Search module"
                />
                <SearchIcon />
            </div>

            <div className={styles.Divider} />

            <div className={styles.SectionHead}>
                <span className={styles.SectionTitle}>Select Modules</span>
                <div
                    className={styles.SelectAll}
                    role="button"
                    tabIndex={0}
                    onClick={() => (allEnabled ? deselectAll() : selectAll())}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            allEnabled ? deselectAll() : selectAll();
                        }
                    }}
                >
                    <span className={clsx(styles.CheckBox, { [styles.On]: allEnabled })}>{allEnabled ? <CheckTick /> : null}</span>
                    <span className={styles.SelectAllLabel}>Select All</span>
                </div>
            </div>

            <div className={styles.ModuleList} onDragLeave={onListDragLeave}>
                {loading ? <SkeletonCards count={10} /> : null}
                {!loading && items.length === 0 ? <div className={styles.Empty}>No modules match "{searchTerm}"</div> : null}
                {!loading &&
                    items.map((item) => {
                        const noData = !item.hasData && !item.locked;
                        const isDragging = dragId === item.id;
                        const showBefore = dropMark?.id === item.id && dropMark.position === 'before' && dragId !== item.id;
                        const showAfter = dropMark?.id === item.id && dropMark.position === 'after' && dragId !== item.id;
                        return (
                            <div key={item.id} className={styles.CardSlot} onDragOver={onDragOver(item.id)} onDrop={onDrop(item.id)}>
                                {showBefore ? <span className={styles.DropIndicator} aria-hidden /> : null}
                                <div
                                    ref={(el) => {
                                        if (el) cardRefs.current.set(item.id, el);
                                        else cardRefs.current.delete(item.id);
                                    }}
                                    className={clsx(styles.Card, {
                                        [styles.Dragging]: isDragging,
                                        [styles.Locked]: item.locked,
                                        [styles.NoData]: noData,
                                        [styles.Disabled]: !item.enabled && !item.locked,
                                    })}
                                    draggable={!item.locked}
                                    onDragStart={onDragStart(item.id, item.locked)}
                                    onDragEnd={onDragEnd}
                                    onMouseEnter={() => (item.locked || noData) && setTooltipId(item.id)}
                                    onMouseLeave={() => setTooltipId(null)}
                                >
                                    <span className={styles.DragHandle} aria-label="Drag to reorder">
                                        <DragHandleIcon />
                                    </span>
                                    <div className={styles.Body}>
                                        <div className={styles.Category}>{item.category}</div>
                                        <div className={styles.Name}>{item.title}</div>
                                    </div>
                                    {item.locked ? (
                                        <span className={styles.LockIcon} aria-label="Locked">
                                            <LockIcon />
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            className={clsx(styles.Toggle, { [styles.On]: item.enabled })}
                                            onClick={() => toggleModule(item.id)}
                                            aria-label={`Toggle ${item.title}`}
                                            aria-pressed={item.enabled}
                                        >
                                            <span className={styles.Knob} />
                                        </button>
                                    )}
                                    {tooltipId === item.id && (item.locked || noData) ? (
                                        <span className={styles.Tooltip}>
                                            {item.locked
                                                ? item.lockedMessage || 'Unlock this module first to add inside report'
                                                : 'Data not available'}
                                        </span>
                                    ) : null}
                                </div>
                                {showAfter ? <span className={styles.DropIndicator} aria-hidden /> : null}
                            </div>
                        );
                    })}
            </div>
        </aside>
    );
};

export default ModuleRail;
